import { useEffect, useState } from "react";
import { SEPLAT_BRAND_COLORS } from "@sga/shared";
import { request } from "../api.js";

/**
 * The portal's chrome, drawn from the platform's default brand profile.
 *
 * The form app reads branding from `GET /public/forms/:slug` because a form belongs to one
 * programme and may carry its own profile. The portal spans every programme and every year, so it
 * has no one form to read — it asks `GET /public/branding` for the default instead. Same two logo
 * assets, same palette, same footer: the two surfaces cannot drift.
 *
 * Fetched with `useEffect` rather than TanStack Query on purpose — this app ships no query library
 * (docs/01-PRODUCT-SPEC.md §13, 150 KB budget), and one cached GET does not justify adding one.
 */

export interface PortalBranding {
  name: string | null;
  logoLayout: string;
  primaryLogoUrl: string | null;
  secondaryLogoUrl: string | null;
  colors: Record<string, string>;
  fonts: { heading: string; body: string };
  footer: { text: string | null; privacyUrl: string | null; dpoContact: string | null };
}

/**
 * What the portal paints with before the fetch lands, and what it keeps painting with if the fetch
 * fails. A brand request that 500s must cost the applicant a pair of logos, never the page.
 */
export const DEFAULT_BRANDING: PortalBranding = {
  name: null,
  logoLayout: "side_by_side",
  primaryLogoUrl: null,
  secondaryLogoUrl: null,
  colors: { ...SEPLAT_BRAND_COLORS },
  fonts: { heading: "", body: "" },
  footer: { text: null, privacyUrl: null, dpoContact: null },
};

/** The tokens portal.css actually reads. A colour outside this list is ignored, not injected. */
const CSS_VARIABLES = [
  "primary",
  "primaryContrast",
  "accent",
  "surface",
  "background",
  "text",
  "border",
  "error",
] as const;

const cssVariableName = (token: string): string =>
  `--sga-${token.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;

/**
 * Only `#rgb`/`#rrggbb` reaches a style property. The palette is superadmin-editable and lands in
 * a stylesheet variable, so it is checked rather than trusted: without this, a colour of
 * `red;background:url(...)` would be a stored-value injection into the page's own CSS.
 */
const HEX = /^#[0-9A-Fa-f]{3}(?:[0-9A-Fa-f]{3})?$/;

const applyPalette = (colors: Record<string, string>): void => {
  const root = document.documentElement;
  for (const token of CSS_VARIABLES) {
    const value = colors[token];
    if (typeof value === "string" && HEX.test(value)) {
      root.style.setProperty(cssVariableName(token), value);
    }
  }
};

// Module-level, so navigating between screens never refetches chrome that cannot have changed.
let cached: PortalBranding | null = null;

const normalise = (branding: PortalBranding): PortalBranding => ({
  ...DEFAULT_BRANDING,
  ...branding,
  colors: { ...DEFAULT_BRANDING.colors, ...(branding.colors ?? {}) },
  footer: { ...DEFAULT_BRANDING.footer, ...(branding.footer ?? {}) },
});

export const useBranding = (): PortalBranding => {
  const [branding, setBranding] = useState<PortalBranding>(cached ?? DEFAULT_BRANDING);

  useEffect(() => {
    if (cached) return;
    let live = true;
    void request<PortalBranding>("/public/branding")
      .then((result) => {
        cached = normalise(result);
        applyPalette(cached.colors);
        if (live) setBranding(cached);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  return branding;
};
